import React from 'react';

import {
  Box,
  Chip,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { BorderedGrid } from '@shared/components/borderedGrid';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import { truncateText } from '@shared/utils/truncateText';
import { IconArrowDown, IconArrowUp, IconEdit, IconTrash } from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import { PostCallMessageDraft } from '@features/Admin/Campaign/utils/post-call-message-mapper';

interface PostCallMessageCardProps {
  message: PostCallMessageDraft;
  index: number;
  isFirst: boolean;
  isLast: boolean;
  disabled: boolean;
  busy: boolean;
  unknownFields: string[];
  onEdit: (message: PostCallMessageDraft) => void;
  onDelete: (message: PostCallMessageDraft) => void;
  onToggle: (message: PostCallMessageDraft, checked: boolean) => void;
  onMove: (index: number, direction: -1 | 1) => void;
}

const PostCallMessageCard: React.FC<PostCallMessageCardProps> = ({
  message,
  index,
  isFirst,
  isLast,
  disabled,
  busy,
  unknownFields,
  onEdit,
  onDelete,
  onToggle,
  onMove,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <BorderedGrid
      sx={{
        marginBottom: '12px',
        padding: '16px 20px',
        transition: 'border-color 0.2s ease',
        '&:hover': { borderColor: theme.palette.primary.main },
      }}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Stack direction="row" alignItems="center" spacing={1.5} sx={{ mb: 1, flexWrap: 'wrap' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {t('campaignPage.postCallMessages.ruleTitle', { position: index + 1 })}
            </Typography>
            <Chip
              size="small"
              label={
                message.isEnabled
                  ? t('campaignPage.postCallMessages.status.enabled')
                  : t('campaignPage.postCallMessages.status.disabled')
              }
              sx={{
                backgroundColor: message.isEnabled
                  ? theme.palette.success.light
                  : theme.palette.grey[200],
                color: message.isEnabled ? theme.palette.success.dark : theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '11px',
                height: '22px',
              }}
            />
            <Chip
              size="small"
              label={
                message.conditions.length === 0
                  ? t('campaignPage.postCallMessages.alwaysSends')
                  : t('campaignPage.postCallMessages.conditionsCount', {
                      count: message.conditions.length,
                    })
              }
              sx={{
                backgroundColor: theme.palette.grey[200],
                color: theme.palette.grey[700],
                fontWeight: 500,
                fontSize: '11px',
                height: '22px',
              }}
            />
          </Stack>

          <Tooltip title={message.template} placement="bottom-start">
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
                maxWidth: '560px',
                mb: message.conditions.length > 0 || unknownFields.length > 0 ? 1 : 0,
              }}>
              {truncateText(message.template, 80)}
            </Typography>
          </Tooltip>

          {message.conditions.length > 0 ? (
            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
              {message.conditions.map((condition, conditionIndex) => (
                <Chip
                  key={`${condition.field}-${condition.operator}-${conditionIndex}`}
                  size="small"
                  variant="outlined"
                  label={`${condition.field} ${t(
                    `campaignPage.postCallMessages.operators.${condition.operator}`,
                  )}${condition.value ? ` ${condition.value}` : ''}`}
                  sx={{ fontSize: '11px', height: '22px' }}
                />
              ))}
            </Stack>
          ) : null}

          {unknownFields.length > 0 ? (
            <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
              {t('campaignPage.postCallMessages.unknownFields', {
                fields: unknownFields.join(', '),
              })}
            </Typography>
          ) : null}
        </Box>

        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Tooltip title={t('campaignPage.postCallMessages.moveUp')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onMove(index, -1)}
                disabled={disabled || busy || isFirst}>
                <IconArrowUp size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t('campaignPage.postCallMessages.moveDown')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onMove(index, 1)}
                disabled={disabled || busy || isLast}>
                <IconArrowDown size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <FormGroup sx={{ mr: 0.5 }}>
            <FormControlLabel
              sx={{ marginRight: 0, marginLeft: 0 }}
              control={
                <CustomSwitch
                  checked={message.isEnabled}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    onToggle(message, event.target.checked)
                  }
                  disabled={disabled || busy}
                />
              }
              label=""
            />
          </FormGroup>

          <Tooltip title={t('common.edit')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onEdit(message)}
                disabled={disabled || busy}
                sx={{
                  color: theme.palette.primary.main,
                  '&:hover': { backgroundColor: theme.palette.primary.light },
                }}>
                <IconEdit size={18} />
              </IconButton>
            </span>
          </Tooltip>

          <Tooltip title={t('common.delete')}>
            <span>
              <IconButton
                size="small"
                onClick={() => onDelete(message)}
                disabled={disabled || busy}
                sx={{
                  color: theme.palette.error.main,
                  '&:hover': { backgroundColor: theme.palette.error.light },
                }}>
                <IconTrash size={18} />
              </IconButton>
            </span>
          </Tooltip>
        </Stack>
      </Stack>
    </BorderedGrid>
  );
};

export default PostCallMessageCard;
